import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useTranslation } from '@lib/useTypedTranslation';
import { ITranslationKeys } from '@components/i18n/keys';
import Card from '../../../../components/card/card';
import { BreadcrumbLink } from '../../../../components/navigation/breadcrumbLink';
import './deviceAnomalyExaminationPage.css';
import { Loading } from '../../../../components/loading/loading';
import { InvalidPage } from '../../../../components/errors/invalidPage';
import { useWorldRequest } from '../../../../lib/useWorldRequest';
import { useAlignCharts } from '../../../../lib/useAlignCharts';
import { useWorldRoutes } from '../../../../routes/parts/allWorldRoutes';
import { useWorldSettingsContext } from '../../../../context/worldSettings';
import { getDeviceInfo } from '../../../../services/core/devices';
import { encode } from '../../../../lib/base64';
import {
  getDeviceAnomalyEventChart,
  getDeviceAnomalyDataUsageChart,
  IDeviceAnomalyEventDataPoint,
  IDeviceAnomalyDataUsageDataPoint,
  DeviceAnomalyEventName
} from '../../../../services/core/deviceAnomaliesCharts';
import { DeviceInfoContext } from '../../device';
import { DeviceAnomaliesEventChart } from '../compositions/charts/deviceAnomaliesEventChart';
import { DeviceAnomaliesDataUsage } from '../compositions/charts/deviceAnomaliesDataUsage';
import { getLatestAnomalyDate } from '../compositions/charts/lib';
import { AnomalyType } from '../compositions/anomalyTypePicker';
import { DeviceAnomalyDeviceInfo } from './compositions/deviceAnomalyDeviceInfo';
import { anomalySections } from './compositions/sections';
import { getAnomalyDetectionDateRange } from '../../../../lib/anomalyDetectionDateRange';

interface IProps {
  deviceId: string,
  anomalyType: AnomalyType,
  /** Omit to default to the latest day flagged as an anomaly on this device/type's own top chart. */
  initialDate?: number
}

// The top chart's own title reuses the exact wording from the main anomaly charts page, which is
// distinct from the shorter breadcrumb label each section registers in the AnomalyType
// -> section registry (e.g. "Low Power Events" here vs "Low Power" in the breadcrumb).
const CHART_TITLE_KEYS: Partial<Record<AnomalyType, keyof ITranslationKeys['anomalyDetectionBeta']>> = {
  deviceRebooted: 'REBOOTS_EVENTS',
  deviceLowPower: 'LOW_POWER_EVENTS',
  deviceDropped: 'DROPS_EVENTS',
  totalDataUsage: 'TOTAL_DATA_USAGE',
  processCrashed: 'PROCESS_CRASH_EVENTS',
  processHung: 'PROCESS_HANG_EVENTS'
};

type TopChartDataPoint = IDeviceAnomalyEventDataPoint | IDeviceAnomalyDataUsageDataPoint;

export function DeviceAnomalyExaminationPage(props: IProps) {
  const { deviceId, anomalyType } = props;
  // Keying on deviceId/anomalyType forces a full remount of DeviceAnomalyExaminationContent
  // whenever either changes, instead of a same-instance re-render with new props. Section (picked
  // from the anomalySections registry below) is a different component per anomalyType, so a
  // same-instance re-render would otherwise swap it mid-flight: React would dispose the outgoing
  // Section's chart and mount the incoming one a render before topChartLoading (which only flips
  // true from this component's own data-fetching effect) has had any chance to gate that swap
  // behind the shared <Loading> boundary - registering the incoming chart in useAlignCharts' one
  // shared collection alongside the just-disposed outgoing one, which throws "EventDispatcher is
  // disposed" attaching its own axis-sync listener (see useAlignCharts.ts). A full remount instead
  // gives the new deviceId/anomalyType pair its own fresh useAlignCharts collection, with no stale
  // chart reference for it to ever collide with. initialDate is deliberately excluded from the key
  // - a fresh navigation to another date alone re-syncs selectedDate in place instead, below.
  return <DeviceAnomalyExaminationContent key={`${deviceId}:${anomalyType}`} {...props} />;
}

function DeviceAnomalyExaminationContent(props: IProps) {
  const { deviceId, anomalyType, initialDate } = props;
  const { t } = useTranslation('anomalyDetectionBeta');
  const { t: tRedirect } = useTranslation('redirect');
  const routes = useWorldRoutes();
  const { worldSettings } = useWorldSettingsContext();
  const { smartGroupingTimeZone, deviceIdentifier } = worldSettings;
  const timeZone = smartGroupingTimeZone || 'UTC';
  const anomalyDetectionHour = worldSettings.smartGroupingHour || 0;
  const chartDateRange = React.useMemo(
    () => getAnomalyDetectionDateRange(timeZone, anomalyDetectionHour),
    [timeZone, anomalyDetectionHour]
  );

  // Seeded from initialDate (undefined when the caller didn't pin a day - e.g. a link from the
  // top-level anomalies table - in which case latestAnomalyDate below supplies it once the top
  // chart's data has loaded); further non-zero clicks on this page's own top chart update it in
  // place rather than navigating again. The route/StateSwitch keep this page mounted across
  // browser back/forward or a fresh navigation to another date/deviceId/type (the path and
  // StateSwitch index don't change), so these props can change without remounting - the effect
  // below re-syncs selectedDate whenever any of them do, so the page doesn't keep highlighting
  // and fetching a stale day for the newly-navigated-to device/type/date.
  const [selectedDate, setSelectedDate] = useState<number | undefined>(initialDate);
  useEffect(() => setSelectedDate(initialDate), [initialDate, deviceId, anomalyType]);

  const registration = anomalySections[anomalyType];

  // Keeps the top chart's Y-axis the same width as the section's own chart(s) below it (e.g. the
  // battery levels or reboots timeline chart), matching the main anomaly charts page's own use of
  // this hook. 1 (the top chart) plus however many charts the registered section itself renders -
  // registry-driven since sections differ (e.g. processHung's ProcessHangsSection renders a table
  // and no chart at all, so its own count is 0). Falls back to 0 extra charts for an unregistered
  // type, which never reaches this render path anyway (see hasNoData below).
  const { addChart, clearCharts } = useAlignCharts(1 + (registration?.chartCount ?? 0));

  const deviceInfoFetcher = useCallback(() => {
    if (!registration) {
      return undefined;
    }
    return getDeviceInfo(deviceId);
  }, [registration, deviceId]);
  const { data: device, loading: deviceLoading } = useWorldRequest(deviceInfoFetcher, { initialLoading: true });

  // Both the top chart and the section's own chart below it are gated behind this fetcher's own
  // loading state (topChartLoading, via the shared <Loading> wrapper further down, which has no
  // hideChildren and so unmounts everything - top chart included - while true) - a data-only
  // refetch (e.g. timeZone/anomalyDetectionHour changing from world settings, Section's own type
  // unaffected) flips that loading state true and so disposes both charts together, exactly like
  // last24HoursReport.tsx's own same-shaped charts. Clearing here, mirroring that established call
  // site, runs synchronously before setLoading(true) ever does (see useRequest.ts), so it always
  // completes before either chart is disposed, let alone before whichever ones remount once the
  // new data arrives. deviceId/anomalyType changes never reach this race at all - the wrapper above
  // remounts this whole component fresh for those instead (see its own comment).
  const topChartDataFetcher = useCallback(() => {
    if (!registration) {
      return undefined;
    }
    clearCharts();
    const params = { deviceId, timeZone, ...chartDateRange };
    if (anomalyType === 'totalDataUsage') {
      return getDeviceAnomalyDataUsageChart(params);
    }
    return getDeviceAnomalyEventChart({ ...params, eventName: anomalyType as DeviceAnomalyEventName });
  }, [registration, deviceId, timeZone, chartDateRange, anomalyType, clearCharts]);
  const { data: topChartData, loading: topChartLoading } = useWorldRequest<TopChartDataPoint[]>(
    topChartDataFetcher, { initialLoading: true, initialData: [] }
  );

  // The latest day flagged as an anomaly on this device/type's own top chart - both the "no
  // anomaly data at all" check below and, when no explicit date was supplied (e.g. a top-level
  // table link), the default day to examine. By the time this is actually used below, the
  // <Loading> boundary guarantees topChartData has fully loaded (it renders no children at all
  // while either fetch is in flight - see Loading.tsx), so this always reflects the real fetched
  // data, never the empty initialData.
  const latestAnomalyDate = getLatestAnomalyDate(topChartData);

  const hasNoData = !registration || (!topChartLoading && !deviceLoading && (!device || latestAnomalyDate === undefined));

  if (hasNoData) {
    return (
      <Card noPadding={true}>
        <InvalidPage
          message={tRedirect('DEVICE_ANOMALY_NO_DATA')}
          redirect={{
            message: tRedirect('REDIRECT_TO_ANOMALY_DETECTION_BETA'),
            path: routes.core.anomalyDetectionBeta
          }}
        />
      </Card>
    );
  }

  const { titleKey, Section } = registration;
  // Resolved to a real number by this point: hasNoData's own check above already returned early
  // for the only case where latestAnomalyDate could still be undefined (no anomaly data at all),
  // so whenever the caller didn't supply an explicit selectedDate, latestAnomalyDate is it -
  // TypeScript can't see that correlation through the hasNoData boolean, hence the assertion.
  const effectiveDate = (selectedDate ?? latestAnomalyDate) as number;

  return (
    <div data-id="device-anomaly-examination-page">
      <Card noPadding={true}>
        <Loading isLoading={topChartLoading || deviceLoading} transparentOverlay={false} className="device-anomaly-examination-page-loading">
          <div className="device-anomalies-page__header">
            <div className="device-anomalies-page__header-left">
              <h1 className="device-anomalies-page__title">{t('DEVICE_ANOMALY_EXAMINATION_TITLE')}</h1>
              <div className="device-anomalies-page__breadcrumb">
                <BreadcrumbLink to={routes.core.anomalyDetectionBeta} aria-label={t('ANOMALIES_BETA')} className="device-anomalies-page__breadcrumb-link">
                  {t('ANOMALIES_BETA')}
                </BreadcrumbLink>
                <i className="fas fa-chevron-right" />
                <BreadcrumbLink
                  to={`${routes.core.deviceAnomalyDetails}?id=${encodeURIComponent(encode(deviceId))}`}
                  aria-label={device?.[deviceIdentifier]}
                  className="device-anomalies-page__breadcrumb-link device-anomaly-examination-page__breadcrumb-device-link"
                >
                  {device?.[deviceIdentifier]}
                </BreadcrumbLink>
                <i className="fas fa-chevron-right" />
                <span className="device-anomalies-page__device-identifier">{t(titleKey)}</span>
              </div>
            </div>
            <Link
              to={`${routes.core.devicePerformance}?id=${encodeURIComponent(encode(deviceId))}`}
              className="device-anomalies-page__performance-link"
            >
              {t('VIEW_DEVICE_PERFORMANCE')}
            </Link>
          </div>
          {device && (
            <DeviceInfoContext.Provider value={device}>
              <DeviceAnomalyDeviceInfo deviceId={deviceId} />
              <div className="device-anomalies-page__chart-section">
                {anomalyType === 'totalDataUsage' ? (
                  <DeviceAnomaliesDataUsage
                    data={topChartData as IDeviceAnomalyDataUsageDataPoint[]}
                    title={t(CHART_TITLE_KEYS[anomalyType])}
                    cypressId="deviceAnomalyExaminationChart"
                    timeZone={timeZone}
                    onMount={addChart}
                    selectedDate={effectiveDate}
                    onDayClick={setSelectedDate}
                  />
                ) : (
                  <DeviceAnomaliesEventChart
                    data={topChartData as IDeviceAnomalyEventDataPoint[]}
                    title={t(CHART_TITLE_KEYS[anomalyType])}
                    cypressId="deviceAnomalyExaminationChart"
                    timeZone={timeZone}
                    onMount={addChart}
                    selectedDate={effectiveDate}
                    onDayClick={setSelectedDate}
                  />
                )}
              </div>
              <Section deviceId={deviceId} selectedDate={effectiveDate} onChartMount={addChart} />
            </DeviceInfoContext.Provider>
          )}
        </Loading>
      </Card>
    </div>
  );
}
